<script setup lang="ts">
import { CLIENT_EXPERT_URL } from '~/shared/api/client/news/news.const';
import type { INewsItemCategoryDTO } from '~/shared/api/client/news/news.dto';
import { resizeImageSetLayers } from '~/shared/lib/resizeImage';

const props = defineProps<{
  id: number;
  title: string;
  image?: string | null;
  author: string | null;
  textColor: string;
  company: string;
  category?: INewsItemCategoryDTO | null;
}>();

/* Фон один на все брейкпоинты — берём самый крупный размер из стилей. */
const IMAGE_SIZE = 100;

const formattedAuthor = computed(() =>
  props.author ? props.author.replace(/ /g, '<br>') : '',
);

const link = computed(() => `${CLIENT_EXPERT_URL}${props.id}`);
const imageStyle = computed(() =>
  resizeImageSetLayers(props.image, {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    mode: 'fill',
  })
    .map((layer) => `background-image: ${layer}`)
    .join('; '),
);
</script>

<template>
  <NuxtLink
    :to="link"
    :class="$style.item"
    :style="{
      '--text-color': textColor,
    }"
  >
    <div :class="$style.item__author">
      <div
        v-if="props.image"
        :class="$style.item__authorImage"
        :style="imageStyle"
      />
      <div :class="$style.item__info">
        <div :class="$style.item__name" v-html="formattedAuthor" />
        <div :class="$style.item__company">{{ props.company }}</div>
      </div>
    </div>
    <div :class="$style.item__title">
      {{ props.title }}
    </div>

    <PartnerBadge
      :category="props.category"
      with-label
      muted
      :class="$style.item__partnerBadge"
    />
  </NuxtLink>
</template>

<style module lang="scss">
.item {
  display: flex;
  flex-flow: column nowrap;
  padding: var(--space-2-5x);
  width: 100%;
  height: 100%;
  gap: 20px;
  text-decoration: none;

  /* Значок партнёрского материала прижат к правому нижнему углу карточки. */
  &__partnerBadge {
    margin-top: auto;
    align-self: flex-end;
    /*
      Под подписью отступ меньше, чем внутренний отступ карточки: у неё
      по кругу 20px, а под значком нужно 8px.
    */
    margin-bottom: calc(var(--space-1x) - var(--space-2-5x));
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-0-5x);
    justify-content: center;
    text-align: left;
  }

  &__author {
    display: flex;
    flex-direction: row;
    gap: 35px;
    align-items: center;

    &Image {
      display: block;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      background-size: cover;
      flex-shrink: 0;
    }
  }

  &__title {
    @include text_h4;
    color: var(--text-color);
    @include line_clamp(3);
  }

  &__name {
    @include text_body1_large_medium;
    color: var(--text-color);
  }

  &__company {
    @include text_body2;
    color: var(--color-gray-text);
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (min-width: 768px) and (max-width: 1379px) {
  .item {
    justify-content: inherit;
    gap: 23px;
  }
}

@media (max-width: 767px) {
  .item {
    gap: 0;
  }
}

@media (max-width: 479px) {
  .item {
    padding: 20px var(--space-2x);
    gap: var(--space-1-5x);

    &__author {
      gap: var(--space-1-5x);
    }

    &__authorImage {
      width: 80px;
      height: 80px;
    }

    &__title {
      @include text_h5;
    }
  }
}
</style>
